import {
  normalizeGalleryBlockData,
  type GalleryBlockData,
} from '#pages/document/editorjs-types';

export const NP_GALLERY_TAG = 'np-gallery';
export const NP_GALLERY_OPEN_TAG = `<${NP_GALLERY_TAG}>`;
export const NP_GALLERY_CLOSE_TAG = `</${NP_GALLERY_TAG}>`;

export interface ParsedMarkdownGalleryConfig {
  data: GalleryBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

export function stringifyMarkdownGalleryConfig(data: GalleryBlockData): string {
  return JSON.stringify(normalizeGalleryBlockData(data), null, 2);
}

export function parseMarkdownGalleryConfig(
  rawConfig: string,
): ParsedMarkdownGalleryConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeGalleryBlockData({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeGalleryBlockData(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeGalleryBlockData({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse gallery JSON',
    };
  }
}

export function serializeMarkdownGalleryBlock(params: {
  data: GalleryBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_GALLERY_OPEN_TAG}${serializeMarkdownGalleryInnerConfig(params)}${NP_GALLERY_CLOSE_TAG}`;
}

export function serializeMarkdownGalleryInnerConfig(params: {
  data: GalleryBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return params.parseError && params.rawConfig?.trim()
    ? normalizeRawConfig(params.rawConfig)
    : minifyJsonConfig(normalizeGalleryBlockData(params.data));
}

export function createDefaultMarkdownGalleryValue(): string {
  return serializeMarkdownGalleryBlock({
    data: normalizeGalleryBlockData({}),
  });
}
